/**
 * Material editor material parameters GUI.
 * @module
 */
import { bitCheck, bitSetTo } from "common/utils/bitfield";
import type { GUIController } from "dat.gui";
import { HatchMaterialId } from "geom/hatchMaterialId";
import {
  type DesignMaterialParams,
  DmBlendModes,
  DmLayerSrcIDs,
  DmMaskIDs,
  DmMaskUvTransformIDs,
  DmProceduralHatchMaterialIds,
  DmRandomTilesModes,
  DmRoundSidesModes,
  DmSlopeAxises,
  DmSplitDirections,
  DmTransparencyModes,
  DmUvTransformIDs,
} from "./designMaterialTypes";
import { heightMapTextureNames, maskTextureNames, normalMapNames, textureNames } from "./textureNames";

const hatchMaterialIdStrings = Object.values(HatchMaterialId).filter((value) => typeof value === "string") as string[];

function setFolderTitleBackground(folder: dat.GUI, backgroundColor: string) {
  const titleElements = folder.domElement.getElementsByClassName("title");
  if (titleElements.length > 0) {
    (titleElements.item(0) as HTMLElement).style.backgroundColor = backgroundColor;
  }
}

export const layersUIColor = "#005544";
export const layersUIColorLight = "#008866";
export const warningUIColor = "#aa7700";
export const warningStrongUIColor = "#bb0000";

export function initMaterialEditorGUI(
  gui: dat.GUI,
  materialParams: DesignMaterialParams,
  disposeCalls: (() => void)[],
  onUpdate: (addToUndoStack: boolean) => void,
) {
  const addFolder = (name) => {
    const folder = gui.addFolder(name);
    disposeCalls.push(() => gui.removeFolder(folder));
    return folder;
  };

  const addColorParam = (parent, dataSrc, name) => {
    const colorControl = parent.addColor(dataSrc, name);

    colorControl.onFinishChange(() => onUpdate(true));
    colorControl.onChange(() => onUpdate(false));

    // This is a hack to prevent the color selector from disappearing when mouse moves from the color input to the color selector.
    // This is needed because we've also made the dat.gui elements more compact in the material editor.
    colorControl.domElement.querySelector(".selector").style.marginTop = "21px";
    return colorControl;
  };

  const addNumberParam = (parent, dataSrc, name, min = 0, max = 1, step = 0.01) => {
    const ctrl = parent.add(dataSrc, name, min, max, step);

    const textInput = ctrl.domElement.children[0].children[0];

    if (!textInput) {
      ctrl.listen();
      ctrl.onFinishChange(() => {
        onUpdate(true);
      });
      ctrl.onChange(() => {
        // When the slider is still being dragged, we want to update the 3D scene without adding to the undo stack.
        onUpdate(false);
      });
      return;
    }

    // if present, replace the text input with a new one that
    // doesn't have a mouseMove handler that interferes
    // selection

    ctrl.domElement.children[0].removeChild(textInput);
    const clone: HTMLInputElement = textInput.cloneNode(true);
    ctrl.domElement.children[0].appendChild(clone);

    const setValue = () => {
      const attempted = parseFloat(clone.value);
      if (!isNaN(attempted)) {
        ctrl.setValue(attempted);
        clone.value = dataSrc[name];
        onUpdate(true);
      }
    };

    const originalUpdateDisplay = ctrl.updateDisplay;
    ctrl.updateDisplay = () => {
      originalUpdateDisplay.call(ctrl);
      clone.value = dataSrc[name];
      return ctrl;
    };

    ctrl.onFinishChange(() => {
      onUpdate(true);
    });

    // update on slider input
    ctrl.onChange(() => {
      clone.value = dataSrc[name];
      onUpdate(false);
    });

    // change on blur
    clone.addEventListener("blur", () => {
      setValue();
    });

    // change on enter
    clone.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        setValue();
      }
    });
  };

  const addBitFieldParam = (parent, dataSrc: any, name: string, numBits: number, bitNamePrefix: string) => {
    const folder = parent.addFolder(name);
    const fieldState = {};
    for (let bitIndex = 0; bitIndex < numBits; ++bitIndex) {
      const bitName = bitNamePrefix + (bitIndex + 1);
      fieldState[bitName] = bitCheck(dataSrc[name], bitIndex);
      const bitControl = folder.add(fieldState, bitName);

      bitControl.onChange(() => {
        dataSrc[name] = bitSetTo(dataSrc[name], bitIndex, fieldState[bitName]);
        onUpdate(true);
      });

      const originalUpdateDisplay = bitControl.updateDisplay;
      bitControl.updateDisplay = () => {
        fieldState[bitName] = bitCheck(dataSrc[name], bitIndex);
        originalUpdateDisplay.call(bitControl);
        return bitControl;
      };
    }
  };

  const addArrayParam = (parent, dataSrc, name, values) => {
    const control = parent.add(dataSrc, name, values);
    control.onChange(() => {
      onUpdate(true);

      // The value can only be changed by updateDisplay() if it's defocused, so defocus. Undo changes values via updateDisplay().
      control.domElement.querySelector("select").blur();
    });
    return control;
  };

  const addParam = (parent, dataSrc, name) => {
    const control = parent.add(dataSrc, name);
    control.onChange(() => onUpdate(true));
    return control;
  };

  const folderDmParams = addFolder("Material Parameters");

  // Layer colors can be linked together with the base color, reflect this in the GUI!
  const linkableColors: GUIController[] = [];
  function updateLinkableColors(skip: GUIController) {
    for (const color of linkableColors) {
      if (color !== skip) {
        color.updateDisplay();
      }
    }
    onUpdate(false);
  }

  const color = addColorParam(folderDmParams, materialParams, "color");
  color.domElement.parentElement.parentElement.style.backgroundColor = layersUIColor;
  linkableColors.push(color);
  color.onChange(() => updateLinkableColors(color));
  const useLegacyGamma = addParam(folderDmParams, materialParams, "useLegacyGamma");
  useLegacyGamma.domElement.parentElement.parentElement.style.backgroundColor = layersUIColor;
  addArrayParam(folderDmParams, materialParams, "transparency", DmTransparencyModes);
  addNumberParam(folderDmParams, materialParams, "opacity");
  addNumberParam(folderDmParams, materialParams, "roughness");
  addNumberParam(folderDmParams, materialParams, "metalness");
  addNumberParam(folderDmParams, materialParams, "side", 0, 2, 1);
  addNumberParam(folderDmParams, materialParams, "layerHeightFilterWidth", 0.1, 4.0);

  addNumberParam(folderDmParams, materialParams, "rotateAllLayers", -180, 180);

  addArrayParam(folderDmParams, materialParams, "hatchMaterialId", [
    ...DmProceduralHatchMaterialIds,
    ...hatchMaterialIdStrings,
  ]);
  addParam(folderDmParams, materialParams, "hatchMaterialIgnoreVaryHeight");
  addParam(folderDmParams, materialParams, "hatchMaterialIgnoreBevels");

  addParam(folderDmParams, materialParams, "thumbnailDiagonalCamera");

  folderDmParams.open();

  const folderExpensiveParams = addFolder("Expensive Material Parameters");
  setFolderTitleBackground(folderExpensiveParams, warningUIColor);
  addNumberParam(folderExpensiveParams, materialParams, "reflectivity", 0.0, 1.75);
  addParam(folderExpensiveParams, materialParams, "useSpecularColor");
  addColorParam(folderExpensiveParams, materialParams, "specularColor");
  addParam(folderExpensiveParams, materialParams, "useEnvMap");
  addNumberParam(folderExpensiveParams, materialParams, "envMapIntensity", 0.0, 5.0);

  const folderExperimentalParams = addFolder("Experimental/Debug Material Parameters");
  setFolderTitleBackground(folderExperimentalParams, warningStrongUIColor);
  addNumberParam(folderExperimentalParams, materialParams, "clearcoat");
  addNumberParam(folderExperimentalParams, materialParams, "clearcoatRoughness");
  addNumberParam(folderExperimentalParams, materialParams, "transmission");
  addNumberParam(folderExperimentalParams, materialParams, "layerHeightAoStrength");
  addParam(folderExperimentalParams, materialParams, "disableSSAO");

  const folderNormalMapParams = addFolder("Normal Map Parameters");
  addParam(folderNormalMapParams, materialParams, "normalMapEnabled");
  addArrayParam(folderNormalMapParams, materialParams, "normalMapName", ["", ...normalMapNames]);
  addNumberParam(folderNormalMapParams, materialParams, "normalMapStrength");
  addNumberParam(folderNormalMapParams, materialParams, "normalMapScaleU", 1, 3000);
  addNumberParam(folderNormalMapParams, materialParams, "normalMapScaleV", 1, 3000);
  addNumberParam(folderNormalMapParams, materialParams, "normalMapTranslateU", 0, 24);
  addNumberParam(folderNormalMapParams, materialParams, "normalMapTranslateV", 0, 24);
  addNumberParam(folderNormalMapParams, materialParams, "normalMapRotation", -180, 180);

  const folderHeightMapParams = addFolder("Parallax Height Map");
  addParam(folderHeightMapParams, materialParams, "heightMapEnabled");
  addArrayParam(folderHeightMapParams, materialParams, "heightMapName", [
    "",
    ...heightMapTextureNames,
    ...maskTextureNames,
    ...textureNames,
  ]);
  addNumberParam(folderHeightMapParams, materialParams, "heightMapScaleU", 1, 3000);
  addNumberParam(folderHeightMapParams, materialParams, "heightMapScaleV", 1, 3000);
  addNumberParam(folderHeightMapParams, materialParams, "heightMapDepth", 0, 5);
  addNumberParam(folderHeightMapParams, materialParams, "heightMapRotation", -180, 180);

  const folderMaskTexParams = addFolder("Texture based SDF Mask");
  addArrayParam(folderMaskTexParams, materialParams, "mainMaskTexName", ["", ...maskTextureNames, ...textureNames]);

  const folderSDFParams = addFolder("SDF Parameters");
  addParam(folderSDFParams, materialParams.sdf.common, "maskOnly");
  addNumberParam(folderSDFParams, materialParams.sdf.common, "rotateAllSdfPatterns", -180, 180);
  addParam(folderSDFParams, materialParams.sdf.common, "autoGenerateHatchIfMaskOnly");
  addParam(folderSDFParams, materialParams.sdf.common, "useShadows");
  addParam(folderSDFParams, materialParams.sdf.common, "useAO");
  addNumberParam(folderSDFParams, materialParams.sdf.common, "randomMaskSeed", 0, 100);
  addNumberParam(folderSDFParams, materialParams.sdf.common, "depth", 0.05, 1.5);
  addNumberParam(folderSDFParams, materialParams.sdf.common, "aoStrength", 0.0, 5.0);
  addNumberParam(folderSDFParams, materialParams.sdf.common, "aoTolerance", 0.01, 2.5);
  addNumberParam(folderSDFParams, materialParams.sdf.common, "edgeWearStrength", 0.0, 3.0);
  addNumberParam(folderSDFParams, materialParams.sdf.common, "edgeWearSpread", 0.0, 1.0);
  addNumberParam(folderSDFParams, materialParams.sdf.common, "thumbnailAoStrength", 0.0, 5.0);
  addNumberParam(folderSDFParams, materialParams.sdf.common, "thumbnailAoTolerance", 0.01, 2.5);
  addArrayParam(folderSDFParams, materialParams.sdf.common, "slopeAxis", DmSlopeAxises);
  addArrayParam(folderSDFParams, materialParams.sdf.common, "randomTilesMode", DmRandomTilesModes);

  addNumberParam(folderSDFParams, materialParams.sdf.randomTiles, "tileVariability", 0, 1.0);
  addNumberParam(folderSDFParams, materialParams.sdf.randomTiles, "minTileVariability", 0, 1.0);

  addArrayParam(folderSDFParams, materialParams.sdf.randomTiles, "tileSplitDirection", DmSplitDirections);
  addNumberParam(folderSDFParams, materialParams.sdf.randomTiles, "tileSplitProbability", 0, 1.0);
  addNumberParam(folderSDFParams, materialParams.sdf.randomTiles, "alwaysSplitTilesAboveWidth", 0, 100.0);
  addNumberParam(folderSDFParams, materialParams.sdf.randomTiles, "alwaysSplitTilesAboveHeight", 0, 100.0);
  addNumberParam(folderSDFParams, materialParams.sdf.randomTiles, "minTileHeight", 0, 50.0);
  addNumberParam(folderSDFParams, materialParams.sdf.randomTiles, "minTileWidth", 0, 50.0);

  for (let i = 0; i < materialParams.sdf.patterns.length; ++i) {
    const folderSDFPattern = addFolder(`SDF Pattern ${i + 1}`);
    const pattern = materialParams.sdf.patterns[i];
    addParam(folderSDFPattern, pattern, "enabled");
    addNumberParam(folderSDFPattern, pattern, "slope", -45, 45);
    addNumberParam(folderSDFPattern, pattern, "rotation", -180, 180);
    addParam(folderSDFPattern, pattern, "varyHeight");
    addNumberParam(folderSDFPattern, pattern, "varyHeightScale", 0.0, 1);
    addParam(folderSDFPattern, pattern, "cutXfromY");
    addNumberParam(folderSDFPattern, pattern, "cutXfromYScale", 0.0, 1);
    addParam(folderSDFPattern, pattern, "continuousX");
    addParam(folderSDFPattern, pattern, "continuousY");
    addNumberParam(folderSDFPattern, pattern, "xSize", 0.05, 200);
    addNumberParam(folderSDFPattern, pattern, "ySize", 0.05, 200);
    addNumberParam(folderSDFPattern, pattern, "zSize", 0.02, 3);
    addNumberParam(folderSDFPattern, pattern, "xOffset", -200, 200);
    addNumberParam(folderSDFPattern, pattern, "yOffset", -200, 200);
    addNumberParam(folderSDFPattern, pattern, "xDistance", 0.1, 200);
    addNumberParam(folderSDFPattern, pattern, "yDistance", 0.1, 200);
    addParam(folderSDFPattern, pattern, "useBevels");
    addNumberParam(folderSDFPattern, pattern, "bevelDistanceX", 0.0, 3);
    addNumberParam(folderSDFPattern, pattern, "bevelDistanceY", 0.0, 3);
    addNumberParam(folderSDFPattern, pattern, "bevelDistanceZ", 0.0, 3);
    addArrayParam(folderSDFPattern, pattern, "roundSidesMode", DmRoundSidesModes);
    addNumberParam(folderSDFPattern, pattern, "roundStrength", 0.0, 10);
    addNumberParam(folderSDFPattern, pattern, "maskRandomSeedIndex", 0, 7, 1);
  }

  for (let i = 0; i < materialParams.sdf.distortions.length; ++i) {
    const folderSDFDistortion = addFolder(`SDF Distortion ${i + 1}`);
    const distortion = materialParams.sdf.distortions[i];
    addParam(folderSDFDistortion, distortion, "enabled");
    addNumberParam(folderSDFDistortion, distortion, "periodX", 0.1, 50);
    addNumberParam(folderSDFDistortion, distortion, "periodY", 0.1, 50);
    addNumberParam(folderSDFDistortion, distortion, "strengthX", 0, 10);
    addNumberParam(folderSDFDistortion, distortion, "strengthY", 0, 10);
    addNumberParam(folderSDFDistortion, distortion, "strengthZ", 0, 10);
    addNumberParam(folderSDFDistortion, distortion, "randomness", 0, 1);
  }

  for (let i = 0; i < 4; i++) {
    const layer = materialParams.layers[i];
    const folder = addFolder(`Layer ${i}`);
    setFolderTitleBackground(folder, layersUIColor);

    addParam(folder, layer, "enabled");
    addArrayParam(folder, layer, "srcID", DmLayerSrcIDs);
    addArrayParam(folder, layer, "uvTransformID", DmUvTransformIDs);
    addArrayParam(folder, layer, "textureName", [...textureNames]);
    addArrayParam(folder, layer, "blendMode", DmBlendModes);
    addNumberParam(folder, layer, "strength", 0, 1);
    addNumberParam(folder, layer, "colorInfluence", 0, 1);
    addArrayParam(folder, layer, "maskID", DmMaskIDs);
    addArrayParam(folder, layer, "maskUvTransformID", DmMaskUvTransformIDs);
    addNumberParam(folder, layer, "sdfMaskRandomness", 0, 2);
    addNumberParam(folder, layer, "sdfMaskRandomAverage", 0, 1);

    // Note that we have 12 patterns, but we only support setting the mask for 8 - the last 3 patterns share the same mask bit due to WebGL 1.0 limitations.
    addBitFieldParam(folder, layer, "sdfMaskPatterns", 8, "pattern ");

    addNumberParam(folder, layer, "sdfMaskDepthOverlay", 0, 1);
    addNumberParam(folder, layer, "maskEdgeWearOverlay", 0, 1);
    addNumberParam(folder, layer, "randomMaskUniqueness", 0, 1);
    addParam(folder, layer, "enableMaskThreshold");
    addNumberParam(folder, layer, "maskThreshold", 0, 1);
    addParam(folder, layer, "invertMask");

    // TODO: Instead of invertInput, we could have a button for reversing the color gradient.

    // TODO: It would be good to have a better gradient editor.
    // Simplest improvement would be buttons for adding/removing color stops in the middle of the gradient: adding a color would copy the value of color1 to color2 etc.
    // Problem with this is that we can't update dat.gui fields using the listen() function because of customizations we do in addNumberParam.
    // Maybe we should try to make a more fully-featured custom gradient editor?
    addParam(folder, layer, "invertInput");
    addNumberParam(folder, layer, "colorCount", 2, 4, 1);

    function layerColorController(colorKey, useBaseColorKey) {
      const baseColor = addParam(folder, layer, useBaseColorKey);
      const color = addColorParam(folder, layer, colorKey);
      if (layer[useBaseColorKey]) {
        color.object = materialParams;
        color.property = "color";
        color.updateDisplay();
      }
      linkableColors.push(color);
      color.onChange(() => updateLinkableColors(color));

      const updateLayerColorWithBaseColorChoice = () => {
        if (layer[useBaseColorKey]) {
          color.object = materialParams;
          color.property = "color";
          color.updateDisplay();
        } else {
          color.object = layer;
          color.property = colorKey;
          color.updateDisplay();
        }
      };

      const originalUpdateDisplay = baseColor.updateDisplay;
      baseColor.updateDisplay = () => {
        originalUpdateDisplay.call(baseColor);
        updateLayerColorWithBaseColorChoice();
        return baseColor;
      };
      baseColor.onChange(() => {
        updateLayerColorWithBaseColorChoice();
        onUpdate(true);
      });
    }

    layerColorController("color0", "useBaseColorAsColor0");
    layerColorController("color1", "useBaseColorAsColor1");
    layerColorController("color2", "useBaseColorAsColor2");
    layerColorController("color3", "useBaseColorAsColor3");
    addNumberParam(folder, layer, "color0Pos", 0, 1);
    addNumberParam(folder, layer, "color1Pos", 0, 1);
    addNumberParam(folder, layer, "color2Pos", 0, 1);
    addNumberParam(folder, layer, "color3Pos", 0, 1);

    addNumberParam(folder, layer, "scaleU", 1, 3000);
    addNumberParam(folder, layer, "scaleV", 1, 3000);
    addNumberParam(folder, layer, "translateU", 0, 24);
    addNumberParam(folder, layer, "translateV", 0, 24);
    addNumberParam(folder, layer, "rotate", -180, 180, 1);
    addNumberParam(folder, layer, "roughnessInfluence", -1.0, 1.0);
    addNumberParam(folder, layer, "metalnessInfluence", -1.0, 1.0);
    addParam(folder, layer, "useForHeight");
    addNumberParam(folder, layer, "heightInfluence", -1.5, 1.5);
    addNumberParam(folder, layer, "randomUvOffsetStrength", 0.0, 1.0);
  }

  for (let i = 0; i < 4; i++) {
    const mask = materialParams.masks[i];
    const folder = addFolder(`Mask ${i}`);
    setFolderTitleBackground(folder, layersUIColor);

    addArrayParam(folder, mask, "textureName", [...textureNames]);
    addNumberParam(folder, mask, "scaleU", 1, 3000);
    addNumberParam(folder, mask, "scaleV", 1, 3000);
    addNumberParam(folder, mask, "translateU", 0, 1200);
    addNumberParam(folder, mask, "translateV", 0, 1200);
    addNumberParam(folder, mask, "rotate", -180, 180, 1);
    addNumberParam(folder, mask, "strength", 0, 1);
    addNumberParam(folder, mask, "contrast", 0, 1);
  }
}
